import _ from 'lodash';

import CommonData from 'app/common/data';
import DeveloperUtils from 'app/common/utils/DeveloperUtils';
import z from 'app/common/z';
import Errors, { resourceNames } from 'app/errors';
import fetch, {
  deleteJson,
  fetchJson,
  patchJson,
  postJson,
  putJson,
  sendFormData,
} from 'app/common/fetch';
import { BUILDER_URL, issueStatuses } from 'app/developer/constants';

const developerAppData = CommonData.backboneModelAsData({
  name: 'DeveloperApp',
  testModelExists() {
    return z.app.developer.app ? true : false;
  },
  createModel() {
    z.app.developer.app = new z.model.DeveloperApp();
  },
  model() {
    return z.app.developer.app;
  },
  modelToData(appModel) {
    return { ...appModel.toJSON(), isLoaded: !!appModel.loaded };
  },
});

developerAppData.reload = () => {
  if (z.app.developer.app) {
    return z.app.developer.app.promiseFetch({
      cache: false,
    });
  }
  return Promise.resolve();
};

const Data = {};

Data.zapTemplates = {
  deleteTemplate(templateId) {
    return deleteJson(`/api/developer/v1/guided-zaps/${templateId}`).catch(
      response => {
        throw new Errors.DataDeleteError({
          resource: resourceNames.zapTemplates,
          response,
        });
      }
    );
  },
};

Data.devApp = developerAppData;

Data.loadAppEvents = (appId, filters) => {
  const apiUrl = `${z.cfg.devapi}/apps/${appId}/events`;

  const url = fetch.url(apiUrl, filters);
  return fetchJson(url)
    .then(results => {
      return results.events;
    })
    .catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.devAppEvents,
        response,
      });
    });
};

Data.loadAppEventStats = (appId, filters) => {
  const apiUrl = `${z.cfg.devapi}/apps/${appId}/events/stats`;

  const url = fetch.url(apiUrl, filters);
  return fetchJson(url).catch(response => {
    throw new Errors.DataGetError({
      resource: resourceNames.devAppEventStats,
      response,
    });
  });
};

Data.loadAppIssues = (appId, isCliApp = false) => {
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/issues`
    : `${z.cfg.devapi}/apps/${appId}/issues`;
  return fetchJson(url)
    .then(resp => {
      return resp.results;
    })
    .catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.devAppIssues,
        response,
      });
    });
};

Data.saveCCContactEmail = (appId, isCliApp, email) => {
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/notification-settings`
    : `${z.cfg.devapi}/apps/${appId}/notification-settings`;
  return patchJson(url, { ccContact: { email } }).catch(response => {
    throw new Errors.PlatformError({
      summary: 'Failed to update developer notification settings.',
      response,
    });
  });
};

Data.changeDeveloperNotifications = (appId, isCliApp, shouldNotify) => {
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/notification-settings`
    : `${z.cfg.devapi}/apps/${appId}/notification-settings`;
  return patchJson(url, { should_notify: shouldNotify }).catch(response => {
    throw new Errors.PlatformError({
      summary: 'Failed to update developer notification settings.',
      response,
    });
  });
};

Data.loadAppNotificationSettings = (appId, isCliApp = false) => {
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/notification-settings`
    : `${z.cfg.devapi}/apps/${appId}/notification-settings`;
  return fetchJson(url);
};

Data.loadAppIssue = (appId, issueId, isCliApp = false) => {
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/issues/${issueId}`
    : `${z.cfg.devapi}/apps/${appId}/issues/${issueId}`;
  return fetchJson(url).catch((/* response */) => {
    window.location = `${BUILDER_URL}/app/${appId}/issues`; // eslint-disable-line
  });
};

Data.loadLokiTags = () => {
  const url = fetch.url(`${z.cfg.devapi}/tags`);
  return fetchJson(url)
    .then(resp => {
      return resp;
    })
    .catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.devAppLokiTags,
        response,
      });
    });
};

Data.toggleIssueAction = (
  appId,
  issueId,
  category,
  actionKey,
  isAdding,
  isCliApp = false
) => {
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/issues/${issueId}/${category.toLowerCase()}/${actionKey}`
    : `${
        z.cfg.devapi
      }/apps/${appId}/issues/${issueId}/${category.toLowerCase()}/${actionKey}`;
  const handler = isAdding ? postJson : deleteJson;
  return handler(url).catch(response => {
    const ErrorType = isAdding ? Errors.DataPostError : Errors.DataDeleteError;
    throw new ErrorType({
      resource: resourceNames.devAppIssueActionTag,
      response,
    });
  });
};

Data.toggleIssueTag = (appId, issueId, tagId, isAdding, isCliApp = false) => {
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/issues/${issueId}/tags/${tagId}`
    : `${z.cfg.devapi}/apps/${appId}/issues/${issueId}/tags/${tagId}`;
  const handler = isAdding ? postJson : deleteJson;
  return handler(url).catch(response => {
    const ErrorType = isAdding ? Errors.DataPostError : Errors.DataDeleteError;
    throw new ErrorType({
      resource: resourceNames.devAppIssueTag,
      response,
    });
  });
};

Data.loadTaggableUsers = appId => {
  const url = fetch.url(`${z.cfg.devapi}/apps/${appId}/taggable-users`);
  return fetchJson(url)
    .then(resp => {
      return resp;
    })
    .catch(response => {
      console.error(response);
    });
};

Data.loadAppStatus = (appId, type) => {
  return fetchJson(`${z.cfg.devapi}/apps/${appId}/status?type=${type}`)
    .then(result => {
      if (result && result.status === 'failed') {
        const action = type === 'deploy' ? 'migrating' : 'deleting';

        // This will be caught in the .catch() below
        throw new Errors.PlatformError({
          summary: `Failed ${action} app ${appId}: ${result.message}`,
          response: result,
        });
      }

      return result;
    })
    .catch(response => {
      // If something really funky happened
      if (!response) {
        throw new Errors.PlatformError({
          summary: `An unknown error has occurred fetching the ${type} status for app ${appId}. Please refresh.`,
        });
      }

      // If it's a "response" error type
      if (response.headers && response.responseText) {
        // Ignore 404s (if when we try to get the status, the app no longer exists, we'll get it)
        if (response.status !== 404) {
          throw new Errors.PlatformError({
            summary: `An error has occurred fetching the ${type} status for app ${appId}: ${response.responseText}. Please try again.`,
            response,
          });
        }
      } else {
        // Otherwise it's a message, thrown by the above .then()
        throw new Errors.PlatformError({ summary: response.toString() });
      }
    });
};

Data.loadAppDeployHistory = appId => {
  return fetchJson(`${z.cfg.devapi}/apps/${appId}/migrations`).catch(
    response => {
      if (response.status === 405) {
        // Tried to load deploy history for a non-public app, just silently ignore
        return [];
      }
      throw new Errors.PlatformError({
        summary:
          'An error occurred fetching the deploy history. Please try again.',
        response,
      });
    }
  );
};

Data.cloneApp = appId => {
  return fetchJson(`${z.cfg.devapi}/apps/${appId}/clones`, { method: 'POST' })
    .then(response => {
      // LEGACY: The way Backbone tracks if it needs to load the list of apps again is by this global flag.
      // For now, we set it and move on. Once React/Redux controls dev platform, should be refactored into the store.
      z.app.developer.isAppSetCacheStale = true;

      return response;
    })
    .catch(response => {
      throw new Errors.PlatformError({
        summary: 'An error occurred cloning the app. Please try again.',
        response,
      });
    });
};

Data.deleteApp = (appId, isCliApp = false) => {
  const errorMessage = isCliApp
    ? 'Failed deleting the app. Please confirm it is private, and all versions have no users/zaps.'
    : 'Failed deleting the app. Please confirm it is private, without users/zaps, and is not being deployed.';

  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}`
    : `${z.cfg.devapi}/apps/${appId}`;

  return fetch(url, { method: 'DELETE' })
    .then(response => {
      if (!isCliApp && response.status !== 204) {
        throw new Errors.PlatformError({ summary: errorMessage, response });
      }
      if (isCliApp && response.status !== 200) {
        throw new Errors.PlatformError({ summary: errorMessage, response });
      }
      return { appId };
    })
    .catch(response => {
      throw new Errors.PlatformError({ summary: errorMessage, response });
    });
};

Data.loadDeletedApps = () => {
  return fetchJson(`${z.cfg.devapi}/apps/deletion-queue`)
    .then(response => {
      // LEGACY: The way Backbone tracks if it needs to load the list of apps again is by this global flag.
      // For now, we set it and move on. Once React/Redux controls dev platform, should be refactored into the store.
      z.app.developer.isAppSetCacheStale = true;

      return response;
    })
    .catch(response => {
      throw new Errors.PlatformError({
        summary: 'Failed to get a list of apps currently being deleted.',
        response,
      });
    });
};

Data.updateAssignee = ({ appId, issueId, newAssignee, isCliApp = false }) => {
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/issues/${issueId}`
    : `${z.cfg.devapi}/apps/${appId}/issues/${issueId}`;
  return patchJson(url, { assignee: newAssignee }).catch(response => {
    throw new Errors.PlatformError({
      summary: 'Failed to update assignee.',
      response,
    });
  });
};

Data.submitComment = data => {
  const { appId, issueId, commentId, isCliApp } = data;
  const commentUrl = commentId ? `/${commentId}` : '';
  const url = isCliApp
    ? `/api/platform/cli/apps/${appId}/issues/${issueId}/comments${commentUrl}`
    : `${z.cfg.devapi}/apps/${appId}/issues/${issueId}/comments${commentUrl}`;

  const handler = commentId ? putJson : postJson;
  return handler(url, data).catch(response => {
    console.error(response);
    const verb = commentId ? 'update' : 'create new';
    throw new Errors.PlatformError({
      summary: `Failed to ${verb} comment.`,
      response,
    });
  });
};

Data.requestToClose = data => {
  const { appId, issueId } = data;
  const url = `${z.cfg.devapi}/apps/${appId}/issues/${issueId}`;
  return patchJson(url, {
    status: issueStatuses.CLOSE_REQUESTED,
  }).catch(response => {
    throw new Errors.PlatformError({
      summary: `Failed to request to close App Id ${appId}'s Issue Id ${issueId}.`,
      response,
    });
  });
};

Data.createApp = app => {
  const url = `${z.cfg.devapi}/apps`;

  return postJson(url, app)
    .then(response => {
      // LEGACY: The way Backbone tracks if it needs to load the list of apps again is by this global flag.
      // For now, we set it and move on. Once React/Redux controls dev platform, should be refactored into the store.
      z.app.developer.isAppSetCacheStale = true;

      return response;
    })
    .catch(response => {
      throw new Errors.PlatformError({
        summary: 'Failed to create app.',
        response,
      });
    });
};

const updateAppMeta = (app, version) => {
  const baseUrl =
    version === 'cli'
      ? `/api/platform/cli/apps/${app.id}`
      : `${z.cfg.devapi}/apps/${app.id}`;

  const url = fetch.url(baseUrl, { formId: 'meta' });

  return putJson(url, app).catch(response => {
    throw new Errors.PlatformError({
      summary: 'Failed to update app.',
      response,
    });
  });
};

const updateAppImage = (appId, imageFile, version) => {
  const url =
    version === 'cli'
      ? `/api/platform/cli/apps/${appId}/image`
      : `${z.cfg.devapi}/apps/${appId}/image`;

  const formData = new FormData();
  formData.append('image', imageFile);

  return sendFormData(url, formData).catch(response => {
    throw new Errors.PlatformError({
      summary: 'Failed to upload app image.',
      response,
    });
  });
};

Data.updateAppMeta = app => updateAppMeta(app, 'dev');
Data.updateCliAppMeta = app => updateAppMeta(app, 'cli');

Data.updateAppImage = (appId, imageFile) =>
  updateAppImage(appId, imageFile, 'dev');
Data.updateCliAppImage = (appId, imageFile) =>
  updateAppImage(appId, imageFile, 'cli');

Data.getFormFields = formId => {
  const url = `/api/developer/v2/forms/${formId}`;

  return fetchJson(url).catch(response => {
    throw new Errors.PlatformError({
      summary: 'Failed to get a list of apps currently being deleted.',
      response,
    });
  });
};

Data.loadCliAppStats = (appId, appVersion) => {
  const statsEndpoint = appVersion ? `${appVersion}/stats` : 'stats';
  const url = `/api/platform/cli/apps/${appId}/${statsEndpoint}`;

  return fetchJson(url)
    .then(stats => {
      const listStats = DeveloperUtils.getListStatsFromPlain(stats);

      return {
        plain: stats,
        list: listStats,
      };
    })
    .catch(response => {
      throw new Errors.PlatformError({
        summary: 'Failed to get stats data.',
        response,
      });
    });
};

Data.submitActivation = (appId, data) => {
  const url = `${z.cfg.devapi}/apps/${appId}/activation`;

  return postJson(url, data).catch(response => {
    throw new Errors.PlatformError({
      summary: 'Failed to submit activation form data.',
      response,
    });
  });
};

Data.submitCliActivation = (appId, appVersion, data) => {
  const url = `/api/platform/cli/apps/${appId}/versions/${appVersion}/activate`;

  return postJson(url, data).catch(response => {
    throw new Errors.PlatformError({
      summary: 'Failed to submit activation form data.',
      response,
    });
  });
};

Data.promoteCliApp = (appId, appVersion) => {
  const url = `/api/platform/cli/apps/${appId}/versions/${appVersion}/promote/production`;
  return putJson(url, {}).catch(response => {
    const parsedResponse = response.responseText
      ? JSON.parse(response.responseText)
      : { errors: ['Something went wrong.'] };

    const { activationInfo, errors } = parsedResponse;

    throw new Errors.PlatformError({
      summary: 'Failed to promote CLI app.',
      activationInfo,
      errors: _.flattenDeep(errors),
      response,
    });
  });
};

export default Data;
